import type { VerticalDatumData } from "building-types/columns/columnDatumTypes";
import type {
  ColumnDescriptorTable,
  StructuralColumnDescriptorTable,
  StructuralWoodPostDescriptor,
  ColumnDescriptor,
  ArchitecturalColumnDescriptor,
  StructuralColumnDescriptor,
} from "building-types/columns/columnDescriptorTypes";
import type { ColumnSection } from "building-types/columns/columnTypes";
import { ColumnSubType, StructuralColumnMaterial, StructuralColumnBuilt } from "building-types/columns/columnTypesEnum";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { ColumnViewNoteTable } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { defaultColumnViewNotes } from "doc/settings/defaultViewNoteSettings";
import { defaultRelativeVerticalDatumData } from "../elements/columns/columnDefaults";

export const defaultWoodPostMaterial: ExteriorMaterial = "wallExterior0";

export function isArchitecturalColumnDescriptor(column: ColumnDescriptor): column is ArchitecturalColumnDescriptor {
  return column.subType === ColumnSubType.ARCHITECTURAL;
}

export function isStructuralColumnDescriptor(column: ColumnDescriptor): column is StructuralColumnDescriptor {
  return column.subType === ColumnSubType.STRUCTURAL;
}

export function getColumnTable(columnSettings: ColumnCatalogSettings): ColumnDescriptorTable {
  return columnSettings.columnTable;
}

export function getArchitecturalColumnTable(columnSettings: ColumnCatalogSettings): ColumnDescriptorTable {
  const columnTable: ColumnDescriptorTable = {};

  for (const columnId in columnSettings.columnTable) {
    const column = columnSettings.columnTable[columnId];
    if (isArchitecturalColumnDescriptor(column)) {
      columnTable[columnId] = column;
    }
  }
  return columnTable;
}

export function getStructuralColumnTable(columnSettings: ColumnCatalogSettings): StructuralColumnDescriptorTable {
  const columnTable: StructuralColumnDescriptorTable = {};

  for (const columnId in columnSettings.columnTable) {
    const column = columnSettings.columnTable[columnId];
    if (isStructuralColumnDescriptor(column)) {
      columnTable[columnId] = column;
    }
  }
  return columnTable;
}

export function getArchitecturalColumnOrDefault(
  columnTable: ColumnDescriptorTable,
  columnId: string | undefined,
): ArchitecturalColumnDescriptor {
  if (columnId !== undefined) {
    const column = columnTable[columnId];
    if (column !== undefined && isArchitecturalColumnDescriptor(column)) {
      return column;
    }
  }
  return defaultArchitecturalColumnDescriptor;
}

export function tryGetColumnValue(columnId: string | undefined, profileTable: ColumnDescriptorTable) {
  let columnValue: string | undefined;
  if (columnId) {
    columnValue = !profileTable[columnId] ? `<default assigned>` : columnId;
  }
  return columnValue;
}

export function columnDescriptorCopy(column: ColumnDescriptor, name?: string): ColumnDescriptor {
  if (name === undefined) {
    return { ...column };
  }

  return {
    ...column,
    name,
  };
}

export function architecturalColumnDescriptorNew(
  name: string,
  sections: ColumnSection[],
  viewNotes?: ColumnViewNoteTable,
  elevationNoteEnabled?: boolean,
  elevationNote?: string,
): ArchitecturalColumnDescriptor {
  return {
    catalogItemType: "COLUMN_DESCRIPTOR",
    name,
    entityType: ViewEntityType.COLUMN,
    subType: ColumnSubType.ARCHITECTURAL,
    sections,
    viewNotes,
    elevationNoteEnabled,
    elevationNote,
  };
}

export function structuralWoodPostDescriptorNew(
  name: string,
  postThickness: number,
  postWidth: number,
  verticalDatum: VerticalDatumData = defaultRelativeVerticalDatumData,
  paletteId: ExteriorMaterial = defaultWoodPostMaterial,
  viewNotes: ColumnViewNoteTable = defaultColumnViewNotes,
  elevationNoteEnabled?: boolean,
  elevationNote?: string,
): StructuralWoodPostDescriptor {
  return {
    name,
    catalogItemType: "COLUMN_DESCRIPTOR",
    entityType: ViewEntityType.COLUMN,
    subType: ColumnSubType.STRUCTURAL,
    columnMaterial: StructuralColumnMaterial.WOOD,
    built: StructuralColumnBuilt.POST,
    postThickness,
    postWidth,
    viewNotes,
    elevationNoteEnabled,
    elevationNote,
    verticalDatum,
    paletteId,
  };
}
export const defaultArchitecturalColumnDescriptor: ArchitecturalColumnDescriptor = architecturalColumnDescriptorNew(
  `3" Column`,
  [{ height: 0, width: 3, depth: 3, paletteId: "trimExterior0", taper: false }],
  defaultColumnViewNotes,
);
const defaultWoodPostDescriptor: StructuralWoodPostDescriptor = structuralWoodPostDescriptorNew(
  '6"x6" Wood Post',
  6,
  6,
);

export const defaultColumnsInternal = {
  ["defaultArchitecturalColumnDescriptor"]: defaultArchitecturalColumnDescriptor,
  ["defaultWoodPostDescriptor"]: defaultWoodPostDescriptor,
} as const;

type DefaultColumnsId = keyof typeof defaultColumnsInternal;
export const defaultColumnsInternalIds: ColumnDescriptorTable = defaultColumnsInternal;

export function defaultStructuralColumnId(
  material: StructuralColumnMaterial,
  built: StructuralColumnBuilt,
): DefaultColumnsId {
  switch (material) {
    case StructuralColumnMaterial.WOOD: {
      switch (built) {
        case StructuralColumnBuilt.POST: {
          return "defaultWoodPostDescriptor";
        }
        default:
          assertUnreachable(built);
      }
      break;
    }
    default:
      assertUnreachable(material);
  }
}

export const defaultColumnStructuralColumnId = defaultStructuralColumnId(
  StructuralColumnMaterial.WOOD,
  StructuralColumnBuilt.POST,
);
